import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { SITE } from "@/data/site";
import { joinClub, formatApiError, TERMS_TEXT, MARKETING_TEXT } from "@/lib/clubApi";

const inputCls = "bg-brand-bg border-brand-border focus-visible:ring-brand-accent text-white h-12";

/** Standalone free club sign-up: name, e-mail, preferred branch. Phone/vehicle can be added later. */
export function JoinForm({ compact = false }) {
  const [f, setF] = useState({ name: "", email: "", preferred_location: "cestlice", accept_terms: false, marketing_opt_in: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) return toast.error("Napište prosím své jméno.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return toast.error("Zadejte prosím platný e-mail.");
    if (!f.accept_terms) return toast.error("Pro vstup do klubu je potřeba přijmout pravidla členství.");
    setLoading(true);
    try {
      setDone(await joinClub({ ...f, email: f.email.trim() }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-brand-surface border border-brand-accent p-6 lg:p-8" data-testid="join-success">
        <div className="text-xs uppercase tracking-[0.3em] text-brand-accent">Členské číslo</div>
        <div className="mt-2 font-display font-black text-3xl">{done.member_number}</div>
        <p className="mt-4 text-white/90">{done.message}</p>
        <p className="mt-2 text-sm text-brand-muted">
          Odkaz do účtu platí 15 minut. Kdykoliv později si ho pošlete znovu na stránce „Můj účet“.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="join-form" className={`bg-brand-surface border border-brand-border ${compact ? "p-5" : "p-6 lg:p-8"} space-y-3`}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input data-testid="join-name" placeholder="Jméno a příjmení *" value={f.name} onChange={(e) => up("name", e.target.value)} className={inputCls} autoComplete="name" />
        <Input data-testid="join-email" type="email" placeholder="E-mail *" value={f.email} onChange={(e) => up("email", e.target.value)} className={inputCls} autoComplete="email" />
      </div>
      <div className="grid grid-cols-2 gap-2" data-testid="join-location">
        {SITE.locations.map((l) => (
          <button key={l.key} type="button" onClick={() => up("preferred_location", l.key)} aria-pressed={f.preferred_location === l.key}
            data-testid={`join-location-${l.key}`}
            className={`h-11 text-sm font-semibold border transition-colors ${f.preferred_location === l.key ? "bg-brand-accent text-black border-brand-accent" : "bg-brand-bg text-white border-brand-border hover:border-white/40"}`}>
            {l.name.replace("Praha ", "")}
          </button>
        ))}
      </div>
      <label className="flex items-start gap-3 text-xs text-white/80 cursor-pointer">
        <input type="checkbox" data-testid="join-terms" checked={f.accept_terms} onChange={(e) => up("accept_terms", e.target.checked)} className="mt-0.5 accent-[#00DF82]" />
        <span>{TERMS_TEXT} *</span>
      </label>
      <label className="flex items-start gap-3 text-xs text-brand-muted cursor-pointer">
        <input type="checkbox" data-testid="join-marketing" checked={f.marketing_opt_in} onChange={(e) => up("marketing_opt_in", e.target.checked)} className="mt-0.5 accent-[#00DF82]" />
        <span>{MARKETING_TEXT}</span>
      </label>
      <button type="submit" disabled={loading} data-testid="join-submit" className="w-full h-13 py-4 bg-brand-accent text-black font-bold hover:bg-brand-accentHover transition-colors disabled:opacity-60">
        {loading ? "Odesílám..." : "Vstoupit zdarma"}
      </button>
      <div className="text-[11px] text-brand-muted text-center">Zatím zdarma pro prvních 500 členů — poté se aktivuje cena. Bez předplatného, bez platební karty.</div>
    </form>
  );
}
